import React, { useState, useCallback } from 'react';
import { apolloClient, gql } from 'lib/graphql';
import SectionEdition, { Slot } from 'components/styleguide/section_edition/SectionEdition';
import Sheet from 'components/styleguide/sheet_modal/Sheet';
import InputField from 'components/styleguide/input_field/InputField';
import SelectField from 'components/styleguide/select_field/SelectField';
import { useEditableSheet } from 'components/styleguide/sheet_modal/useEditableSheet';
import Button from 'components/styleguide/button/Button';
import Dialog from 'components/styleguide/dialog/Dialog';
const styles = require('../onboarding-cards.module.scss');

type Option = { value: string | number; label: string };

interface Education {
  id: number;
  degree: string;
  info: string;
  graduation: number;
  institution: Option;
}

interface EducationSectionProps {
  educations: Education[];
  createUrl: string;
  updateUrl: string;
}

const GQL_QUERY_INSTITUTIONS = gql`
  query ($query: String) {
    institutions(query: $query) {
      nodes {
        id
        name
      }
    }
  }
`;

const currentYear = new Date().getFullYear();
const YEAR_OPTIONS: Option[] = Array.from({ length: 81 }, (_, i) => ({
  value: currentYear - i,
  label: String(currentYear - i),
}));

const EducationSection: React.FC<EducationSectionProps> = ({
  educations: initialEducations,
  createUrl,
  updateUrl,
}) => {
  const [educations, setEducations] = useState<Education[]>(initialEducations || []);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);

  const sheet = useEditableSheet({
    url: createUrl,
    method: 'POST',
    payloadKey: 'person_institution',
    onSuccess: (_formData, responseData) => {
      const attrs = responseData.data?.data?.attributes;
      if (!attrs) return;

      const education: Education = {
        id: attrs.id,
        degree: attrs.degree,
        info: attrs.info,
        graduation: attrs.graduation,
        institution: attrs.institution,
      };

      if (editingId != null) {
        setEducations(prev => prev.map(e => e.id === editingId ? education : e));
      } else {
        setEducations(prev => [...prev, education]);
      }
    },
    onDelete: (id: number) => {
      setEducations(prev => prev.filter(e => e.id !== id));
    },
  });

  const status = educations.length > 0 ? 'complete' : 'missing';

  const loadInstitutions = useCallback(async (inputValue: string) => {
    if (!inputValue.trim()) return [];

    const result = await apolloClient.query({
      query: GQL_QUERY_INSTITUTIONS,
      variables: { query: inputValue },
    });

    return result.data.institutions.nodes.map((t: { id: string; name: string }) => ({
      label: t.name,
      value: t.id,
    }));
  }, []);

  const openAdd = () => {
    setEditingId(null);
    sheet.open('Add education', {
      degree: '',
      institution_id: '',
      institution: null,
      info: '',
      graduation: '',
    }, { url: createUrl, method: 'POST' });
  };

  const openEdit = (edu: Education) => {
    setEditingId(edu.id);
    sheet.open('Edit education', {
      degree: edu.degree,
      institution_id: edu.institution?.value || '',
      institution: edu.institution || null,
      info: edu.info || '',
      graduation: edu.graduation,
    }, { url: `${updateUrl}/${edu.id}`, method: 'PATCH' });
  };

  const handleDelete = (id: number) => {
    sheet.handleDelete(`${updateUrl}/${id}`, id);
  };

  const selectedInstitution = sheet.formData.institution as Option | null;
  const [institutionInput, setInstitutionInput] = useState('');

  return (
    <>
      <SectionEdition
        title="Education"
        iconName="graduationCap"
        context="talent"
        status={status}
        onAdd={openAdd}
        allowMultiple={true}
        defaultDeleteConfirmation={{
          title: 'Delete this education?',
          message: "You'll remove it from your profile permanently.",
        }}
      >
        {educations.map(edu => (
          <Slot
            key={edu.id}
            onEdit={() => openEdit(edu)}
            onDelete={() => handleDelete(edu.id)}
          >
            <div className={styles.fieldGridCompact}>
              <p className={styles.inputText}>{edu.degree}</p>
              <p className={styles.label}>
                {edu.institution?.label}
                {edu.graduation && <> &bull; {edu.graduation}</>}
              </p>
            </div>
          </Slot>
        ))}
      </SectionEdition>

      {sheet.isOpen && (
        <Sheet
          title={sheet.sheetTitle}
          onClose={sheet.close}
          context="talent"
          showCancelButton={false}
          continueButtonText="Save"
          onContinue={sheet.handleSave}
        >
          <div className={styles.form}>
            <InputField
              label="Name of Degree"
              placeholder="e.g. Bachelor's of..."
              name="info"
              required={true}
              value={sheet.formData.info || ''}
              onChange={(e) => sheet.handleFormChange(e.target.name, e.target.value)}
              size="M"
              error={sheet.getError('info')}
              context="talent"
            />
            <SelectField
              label="Institution"
              name="institution_id"
              placeholder="e.g. University of Lisbon"
              type="async"
              noOptionsMessage={() => 'Type to search'}
              loadOptions={loadInstitutions}
              value={selectedInstitution}
              inputValue={institutionInput}
              onInputChange={(val: string, { action }: { action: string }) => {
                if (action === 'input-change') setInstitutionInput(val);
                if (action === 'set-value') setInstitutionInput('');
              }}
              onFocus={() => { if (selectedInstitution) setInstitutionInput(selectedInstitution.label); }}
              onChange={(v: any) => { sheet.handleFormChange('institution', v || null); sheet.handleFormChange('institution_id', v?.value || ''); setInstitutionInput(''); }}
              size="M"
              required={true}
              error={sheet.getError('institution_id')}
              context="talent"
            />
            <InputField
              label="Level of Degree"
              placeholder="e.g. Major in..."
              name="degree"
              value={sheet.formData.degree || ''}
              onChange={(e) => sheet.handleFormChange(e.target.name, e.target.value)}
              required={true}
              size="M"
              error={sheet.getError('degree')}
              context="talent"
            />
            <SelectField
              label="Graduation Year"
              name="graduation"
              placeholder="e.g. 2019"
              options={YEAR_OPTIONS}
              value={YEAR_OPTIONS.find(o => o.value === sheet.formData.graduation) || null}
              onChange={(v: any) => sheet.handleFormChange('graduation', v?.value)}
              size="M"
              error={sheet.getError('graduation')}
              context="talent"
            />
            {editingId && (
              <Button
                type="secondary"
                size="M"
                iconLeft="trash"
                context="talent"
                onClick={() => setIsDeleteConfirmOpen(true)}
              >
                Delete education
              </Button>
            )}
          </div>
        </Sheet>
      )}
      <Dialog
        isOpen={isDeleteConfirmOpen}
        title="Delete this education?"
        message="You'll remove it from your profile permanently."
        confirmText="Delete"
        context="talent"
        onClose={() => setIsDeleteConfirmOpen(false)}
        onConfirm={() => {
          setIsDeleteConfirmOpen(false);
          if (editingId) handleDelete(editingId);
        }}
      />
    </>
  );
};

export default EducationSection;
